import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiError, strategiesApi } from "@/lib/api";
import { brokerApi } from "@/lib/brokerApi";
import { INSTRUMENTS, type Instrument } from "@/lib/instrument";
import { openInstrument } from "@/lib/openPosition";
import { strategyDailyConfigApi, type StrategyDailyConfig } from "@/lib/strategyDailyConfigApi";
import type { Strategy, StrategyType } from "@/types/strategy";
import { ControlsBody } from "./StrategyControlsBody";
import { buildPayload, defaultControls, type ControlState } from "./strategyControlsState";
import { STRATEGIES_KEY } from "./useStrategyActions";

const BROKER_STATUS_KEY = ["broker", "status"] as const;
const POSITIONS_KEY = ["broker", "positions"] as const;
const POSITIONS_POLL_MS = 30_000;
const DAILY_CONFIG_KEY = (strategyId: string) => ["daily-config", strategyId] as const;

interface Props {
  strategy: Strategy;
  onSaved: () => void;
  onError: (message: string) => void;
}

interface SwitchControl {
  pending: Instrument | null;
  request: (next: Instrument) => void;
  confirm: () => void;
  cancel: () => void;
}

function useSwitchControl(
  current: Instrument,
  openInstr: Instrument | null,
  applySwitch: (next: Instrument) => void,
): SwitchControl {
  const [pending, setPending] = useState<Instrument | null>(null);
  const request = (next: Instrument): void => {
    if (openInstr === current && next !== openInstr) {
      setPending(next);
      return;
    }
    applySwitch(next);
  };
  const confirm = (): void => {
    if (pending !== null) applySwitch(pending);
    setPending(null);
  };
  return {
    pending,
    request,
    confirm,
    cancel: () => {
      setPending(null);
    },
  };
}

function useSaveMutation(
  strategyId: string,
  strategyType: StrategyType,
  onSaved: () => void,
  onError: (msg: string) => void,
) {
  return useMutation({
    mutationFn: (vars: ControlState) =>
      strategyDailyConfigApi.upsert(strategyId, buildPayload(vars, strategyType)),
    onSuccess: () => {
      onSaved();
    },
    onError: (err) => {
      const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
      onError(`Save failed: ${reason}`);
    },
  });
}

function useSaveDefaultLotsMutation(
  strategyId: string,
  onSaved: () => void,
  onError: (msg: string) => void,
) {
  return useMutation({
    mutationFn: (lots: number) => strategiesApi.update(strategyId, { default_lots: lots }),
    onSuccess: () => {
      onSaved();
    },
    onError: (err) => {
      const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
      onError(`Save default failed: ${reason}`);
    },
  });
}

export function StrategyControlsCard({
  strategy,
  onSaved,
  onError,
}: Readonly<Props>): ReactElement {
  // Load whatever's persisted for today so the form reflects the actual
  // saved values rather than day-plan defaults. 404 = no config yet, that's
  // not an error — fall through to defaults.
  const savedQuery = useQuery({
    queryKey: DAILY_CONFIG_KEY(strategy.id),
    queryFn: async (): Promise<StrategyDailyConfig | null> => {
      try {
        return await strategyDailyConfigApi.get(strategy.id);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    retry: false,
  });
  if (savedQuery.isPending) return <LoadingCard />;
  if (savedQuery.isError) return <LoadErrorCard />;
  return (
    <ControlsForm
      strategy={strategy}
      savedConfig={savedQuery.data}
      onSaved={onSaved}
      onError={onError}
    />
  );
}

function LoadingCard(): ReactElement {
  return (
    <section className="card mb-3 text-sm text-ink-secondary">Loading trading controls…</section>
  );
}

function LoadErrorCard(): ReactElement {
  return (
    <section className="card mb-3 text-sm text-danger">
      Failed to load today&apos;s config. Refresh the page — if it persists, check the backend.
    </section>
  );
}

interface FormProps {
  strategy: Strategy;
  savedConfig: StrategyDailyConfig | null;
  onSaved: () => void;
  onError: (message: string) => void;
}

function useControlState(strategy: Strategy, savedConfig: StrategyDailyConfig | null) {
  const [state, setState] = useState<ControlState>(() => defaultControls(strategy, savedConfig));
  const applySwitch = (next: Instrument): void => {
    const info = INSTRUMENTS[next];
    setState((s) => ({
      ...s,
      instrument: next,
      lots: info.defaultLots,
      initialSl: info.defaultSlPoints,
      trailTrigger: info.defaultTrailTrigger,
      trailAmount: info.defaultTrailAmount,
      trailSlippage: info.defaultTrailSlippage,
      targetPoints: info.defaultTargetPoints,
    }));
  };
  const update = <K extends keyof ControlState>(key: K, value: ControlState[K]): void => {
    setState((s) => ({ ...s, [key]: value }));
  };
  const reset = (): void => {
    // Back to last SAVED state — operator's mental model of "discard my edits".
    setState(defaultControls(strategy, savedConfig));
  };
  return { state, applySwitch, update, reset };
}

function useControlsFormMutations(
  strategy: Strategy,
  onSaved: () => void,
  onError: (m: string) => void,
) {
  const qc = useQueryClient();
  const saveMut = useSaveMutation(
    strategy.id,
    strategy.strategy_type,
    () => {
      void qc.invalidateQueries({ queryKey: DAILY_CONFIG_KEY(strategy.id) });
      onSaved();
    },
    onError,
  );
  const saveDefaultLotsMut = useSaveDefaultLotsMutation(
    strategy.id,
    () => {
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
      onSaved();
    },
    onError,
  );
  return { saveMut, saveDefaultLotsMut };
}

function ControlsForm({
  strategy,
  savedConfig,
  onSaved,
  onError,
}: Readonly<FormProps>): ReactElement {
  const { state, applySwitch, update, reset } = useControlState(strategy, savedConfig);
  const openInstr = useOpenInstrument();
  const { saveMut, saveDefaultLotsMut } = useControlsFormMutations(strategy, onSaved, onError);
  const switchCtl = useSwitchControl(state.instrument, openInstr, applySwitch);
  return (
    <>
      <ControlsBody
        state={state}
        onSwitchInstrument={switchCtl.request}
        onUpdate={update}
        onReset={reset}
        onSave={() => {
          saveMut.mutate(state);
        }}
        onSaveLotsAsDefault={() => {
          saveDefaultLotsMut.mutate(state.lots);
        }}
        savingLotsDefault={saveDefaultLotsMut.isPending}
      />
      <SwitchWarn
        pending={switchCtl.pending}
        current={state.instrument}
        onCancel={switchCtl.cancel}
        onConfirm={switchCtl.confirm}
      />
    </>
  );
}

function useOpenInstrument(): Instrument | null {
  const broker = useQuery({
    queryKey: BROKER_STATUS_KEY,
    queryFn: () => brokerApi.status(),
    refetchInterval: 30_000,
  });
  const connected = broker.data?.connected ?? false;
  const positions = useQuery({
    queryKey: POSITIONS_KEY,
    queryFn: () => brokerApi.positions(),
    enabled: connected,
    refetchInterval: connected ? POSITIONS_POLL_MS : false,
    retry: false,
  });
  if (positions.data === undefined) return null;
  return openInstrument(positions.data.positions);
}

interface SwitchWarnProps {
  pending: Instrument | null;
  current: Instrument;
  onCancel: () => void;
  onConfirm: () => void;
}

function SwitchWarn({
  pending,
  current,
  onCancel,
  onConfirm,
}: Readonly<SwitchWarnProps>): ReactElement {
  const target = pending ?? current;
  return (
    <ConfirmDialog
      open={pending !== null}
      title={`Switch instrument to ${target}?`}
      description={
        <span>
          You have an open <span className="font-medium text-ink">{current}</span> trade. The open
          trade keeps running normally with {current} trailing SL. New ENTRY alerts will route to{" "}
          <span className="font-medium text-ink">{target}</span> until you switch back.
        </span>
      }
      confirmLabel={`Switch to ${target}`}
      icon="ti-arrows-exchange"
      tone="warning"
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
